import { useState, useCallback, useEffect } from "react";
import type { PaceStatus } from "../../lib/revenuePlannerEngine";
import { formatUSD } from "@/utils/formatters";
import { useUnsavedGuard } from "@/context/UnsavedGuardContext";

// =============================================================================
// GoalHeader — Revenue Goal Input + Save + Progress
// =============================================================================

interface GoalHeaderProps {
  goalAmount: number;
  cashSoFar: number;
  paceStatus: PaceStatus;
  paceProjected: number;
  monthLabel: string;
  onSave: (amount: number, notes?: string) => Promise<unknown>;
  onDelete: () => Promise<void>;
  onInputChange: (value: number) => void;
  canEdit: boolean;
}

export default function GoalHeader({
  goalAmount,
  cashSoFar,
  paceStatus,
  paceProjected,
  monthLabel,
  onSave,
  onDelete,
  onInputChange,
  canEdit,
}: GoalHeaderProps) {
  const [input, setInput] = useState(goalAmount > 0 ? goalAmount.toString() : "");
  const [saving, setSaving] = useState(false);

  const parsed = parseFloat(input.replace(/[,$\s]/g, ""));
  const isValid = !isNaN(parsed) && parsed > 0;
  const isDirty = isValid && parsed !== goalAmount;

  // Block silent deploy-reload while an unsaved goal edit is pending. GoalHeader
  // is unmounted when its tab isn't shown, so no open-flag needed.
  useUnsavedGuard(isDirty);

  // Lift typed value up for live preview
  const handleInputChange = useCallback(
    (value: string) => {
      setInput(value);
      const p = parseFloat(value.replace(/[,$\s]/g, ""));
      onInputChange(!isNaN(p) && p > 0 ? p : 0);
    },
    [onInputChange]
  );

  // Sync when saved goal loads from DB — runs once when goalAmount goes from 0 → real value.
  // The `input === ""` guard prevents cascading re-renders (only fires on initial DB load).
  useEffect(() => {
    if (goalAmount > 0 && input === "") {
      setInput(goalAmount.toString()); // eslint-disable-line react-hooks/set-state-in-effect -- one-time sync from async prop
      onInputChange(goalAmount);
    }
  }, [goalAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    if (!isValid || !isDirty) return;
    setSaving(true);
    await onSave(parsed);
    setSaving(false);
  }, [isValid, isDirty, parsed, onSave]);

  const pct = goalAmount > 0 ? Math.min((cashSoFar / goalAmount) * 100, 100) : 0;

  const paceColor =
    paceStatus === "ahead"
      ? "var(--green, #16a34a)"
      : paceStatus === "on_track"
        ? "var(--accent, #2563eb)"
        : paceStatus === "behind"
          ? "var(--red, #dc2626)"
          : "var(--text-tertiary)";

  return (
    <div className="liquid-glass anim-lift rounded-[14px]" style={{ padding: "20px 24px" }}>
      <div className="flex flex-wrap items-center gap-4">
        {/* Month + Goal Input */}
        <div className="flex items-center gap-3">
          <span className="font-semibold" style={{ fontSize: 14, color: "var(--text-primary)" }}>
            {monthLabel} Cash Goal
          </span>
          {canEdit ? (
            <div className="flex items-center gap-0">
              <span
                className="flex items-center justify-center rounded-l-lg border border-r-0 font-medium"
                style={{
                  padding: "7px 10px",
                  fontSize: 13,
                  color: "var(--text-tertiary)",
                  background: "var(--bg)",
                  borderColor: "var(--border-light)",
                }}
              >
                $
              </span>
              <input
                type="text"
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="500,000"
                className="rounded-r-lg border font-medium"
                style={{
                  padding: "7px 12px",
                  fontSize: 13,
                  width: 140,
                  color: "var(--text-primary)",
                  background: "var(--surface)",
                  borderColor: "var(--border-light)",
                  outline: "none",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
              />
            </div>
          ) : (
            <span className="font-bold" style={{ fontSize: 18, color: "var(--text-primary)" }}>
              {goalAmount > 0 ? formatUSD(goalAmount) : "Not set"}
            </span>
          )}
          {canEdit && isDirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="anim-tap font-medium rounded-lg transition-colors"
              style={{
                fontSize: 11,
                padding: "7px 16px",
                color: "white",
                background: "var(--accent)",
                border: "none",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          )}
          {canEdit && goalAmount > 0 && !isDirty && (
            <button
              onClick={onDelete}
              className="anim-tap font-medium rounded-lg transition-colors"
              style={{
                fontSize: 10,
                padding: "5px 10px",
                color: "var(--text-tertiary)",
                background: "transparent",
                border: "1px solid var(--border-light)",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Progress summary */}
        {goalAmount > 0 && (
          <div className="flex items-center gap-4">
            {/* Progress bar */}
            <div className="flex items-center gap-2">
              <div
                className="rounded-full overflow-hidden"
                style={{
                  width: 120,
                  height: 6,
                  background: "var(--border-light)",
                }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: paceColor }}
                />
              </div>
              <span className="font-semibold" style={{ fontSize: 12, color: paceColor }}>
                {pct.toFixed(0)}%
              </span>
            </div>

            {/* Pace projection */}
            <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
              <span style={{ fontWeight: 500 }}>Pace:</span> {formatUSD(paceProjected)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
